import { getWithAuth, type ApiErrorResponse } from "@/lib/api-helpers";

export type SearchItemType = "page" | "database" | "content";
export type SearchableBlockType = "page" | "collection_view" | "content";
export type SearchVisibility = "public" | "restricted" | "private" | "workarea" | "template" | null;
export type SearchSort = "relevance" | "updatedAt" | "createdAt";

export interface SearchOwner {
  id: string;
  name: string;
  email: string;
  image: string;
}

export interface BreadcrumbNode {
  id: string;
  title: string;
}

export interface SearchHit {
  id: string;
  title: string;
  icon: string;
  type: SearchItemType;
  visibility: SearchVisibility | "unknown";
  parentId: string;
  parentPageId: string | null;
  workareaId: string | null;
  owner: SearchOwner;
  breadcrumb: BreadcrumbNode[];
  updatedAt: number;
  createdAt: number;
  /** Highlighted snippet; safe HTML with only `<em>` tags surviving. */
  snippet?: string;
}

export interface SearchResponse {
  success: true;
  hits?: SearchHit[];
  /** Legacy route uses `results`; unified route uses `hits`. */
  results?: SearchHit[];
  total: number;
  tookMs?: number;
  took?: number;
}

/**
 * Date range filter. `field` is the indexed timestamp to filter on; both
 * bounds are unix milliseconds; either bound may be omitted to express an
 * open-ended range. The chip UI sets these together (presets), but the API
 * tolerates partial ranges.
 */
export interface SearchDateRange {
  field: "updatedAt" | "createdAt";
  after?: number;
  before?: number;
}

export interface SearchParams {
  query: string;
  workspaceId?: string;
  limit?: number;
  offset?: number;
  types?: SearchableBlockType[];
  /**
   * One or more workArea ids. A hit matches if its workareaId is in this
   * list (logical OR). The legacy single-string form has been removed —
   * callers always pass arrays now.
   */
  workareaId?: string[];
  titleOnly?: boolean;
  /** One or more creator user ids. Multiple values are OR'd on the server. */
  createdBy?: string[];
  /**
   * One or more page ids. A hit matches if its id OR parentPageId is in the
   * list (i.e. "show me anything inside any of these pages, including the
   * pages themselves").
   */
  parentPageId?: string[];
  /** Restrict results to a date range on `updatedAt` or `createdAt`. */
  dateRange?: SearchDateRange;
  sort?: SearchSort;
  /**
   * Abort signal so stale responses never clobber a newer keystroke. The
   * modal passes a fresh AbortController per keystroke and aborts the old
   * one; the fetch is cancelled and we throw a dedicated error below.
   */
  signal?: AbortSignal;
}

export class SearchAbortedError extends Error {
  constructor() {
    super("Search aborted");
    this.name = "SearchAbortedError";
  }
}

/**
 * Joins a string array for an `?ids=a,b,c` style query param. Returns
 * undefined when the list is empty so we skip the param entirely (avoids
 * `?createdBy=` which the server would otherwise see as a non-undefined
 * empty string).
 */
function joinIds(ids: string[] | undefined): string | undefined {
  if (!ids || ids.length === 0) return undefined;
  const cleaned = ids.map((s) => s.trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(",") : undefined;
}

/**
 * Appends the date-range params if both `field` and at least one bound are
 * meaningful. We always pin the field name in the URL even if only `after`
 * is set so the server can disambiguate (`updatedAt` vs `createdAt`).
 */
function appendDateRange(usp: URLSearchParams, dr: SearchDateRange | undefined): void {
  if (!dr) return;
  const after = typeof dr.after === "number" && Number.isFinite(dr.after) ? dr.after : undefined;
  const before = typeof dr.before === "number" && Number.isFinite(dr.before) ? dr.before : undefined;
  if (after === undefined && before === undefined) return;
  usp.set("dateField", dr.field === "createdAt" ? "createdAt" : "updatedAt");
  if (after !== undefined) usp.set("dateAfter", String(after));
  if (before !== undefined) usp.set("dateBefore", String(before));
}

function buildQuery(params: SearchParams): string {
  const usp = new URLSearchParams();
  usp.set("q", params.query);
  if (typeof params.limit === "number") usp.set("limit", String(params.limit));
  if (typeof params.offset === "number") usp.set("offset", String(params.offset));
  if (params.types?.length) usp.set("types", params.types.join(","));
  const workareaId = joinIds(params.workareaId);
  if (workareaId) usp.set("workareaId", workareaId);
  if (params.titleOnly) usp.set("titleOnly", "true");
  const createdBy = joinIds(params.createdBy);
  if (createdBy) usp.set("createdBy", createdBy);
  const parentPageId = joinIds(params.parentPageId);
  if (parentPageId) usp.set("parentPageId", parentPageId);
  appendDateRange(usp, params.dateRange);
  if (params.sort && params.sort !== "relevance") usp.set("sort", params.sort);
  return usp.toString();
}

function isError(x: unknown): x is ApiErrorResponse {
  return !!x && typeof x === "object" && (x as { isError?: boolean }).isError === true;
}

/**
 * Returns an empty result on non-2xx responses instead of throwing, so the
 * modal can render "No results" gracefully. The only case that bubbles an
 * exception is an explicit abort from the caller.
 */
async function callSearch(path: string, params: SearchParams): Promise<{
  hits: SearchHit[];
  total: number;
  tookMs: number;
}> {
  const qs = buildQuery(params);

  // Manually hook the AbortSignal into the underlying fetch. getWithAuth
  // accepts an options bag and forwards it to fetch().
  const options: RequestInit = params.signal ? { signal: params.signal } : {};
  let raw: SearchResponse | ApiErrorResponse;
  try {
    raw = await getWithAuth<SearchResponse>(`${path}?${qs}`, options);
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") {
      throw new SearchAbortedError();
    }
    throw err;
  }

  if (isError(raw)) {
    return { hits: [], total: 0, tookMs: 0 };
  }

  const hits = Array.isArray(raw.hits) ? raw.hits : Array.isArray(raw.results) ? raw.results : [];
  const tookMs = typeof raw.tookMs === "number" ? raw.tookMs : typeof raw.took === "number" ? raw.took : 0;
  return { hits, total: typeof raw.total === "number" ? raw.total : hits.length, tookMs };
}

export async function searchWorkspace(params: SearchParams & { workspaceId: string }): Promise<{
  hits: SearchHit[];
  total: number;
  tookMs: number;
}> {
  if (!params.workspaceId) return { hits: [], total: 0, tookMs: 0 };
  if (!params.query.trim()) return { hits: [], total: 0, tookMs: 0 };
  return callSearch(`/api/search/workspace/${encodeURIComponent(params.workspaceId)}`, params);
}

export async function searchGlobal(params: SearchParams): Promise<{
  hits: SearchHit[];
  total: number;
  tookMs: number;
}> {
  if (!params.query.trim()) return { hits: [], total: 0, tookMs: 0 };
  return callSearch("/api/search", params);
}

export interface RecentParams {
  workspaceId?: string;
  limit?: number;
  types?: SearchableBlockType[];
  workareaId?: string[];
  createdBy?: string[];
  parentPageId?: string[];
  dateRange?: SearchDateRange;
  signal?: AbortSignal;
}

/**
 * Builds the recent query string. We reuse the same search URL params so
 * the backend can read them identically; the only difference is there is
 * no `q`.
 */
function buildRecentQuery(params: RecentParams): string {
  const usp = new URLSearchParams();
  if (typeof params.limit === "number") usp.set("limit", String(params.limit));
  if (params.types?.length) usp.set("types", params.types.join(","));
  const workareaId = joinIds(params.workareaId);
  if (workareaId) usp.set("workareaId", workareaId);
  const createdBy = joinIds(params.createdBy);
  if (createdBy) usp.set("createdBy", createdBy);
  const parentPageId = joinIds(params.parentPageId);
  if (parentPageId) usp.set("parentPageId", parentPageId);
  appendDateRange(usp, params.dateRange);
  return usp.toString();
}

async function callRecent(path: string, params: RecentParams) {
  const qs = buildRecentQuery(params);
  const options: RequestInit = params.signal ? { signal: params.signal } : {};
  let raw: SearchResponse | ApiErrorResponse;
  try {
    raw = await getWithAuth<SearchResponse>(qs ? `${path}?${qs}` : path, options);
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") {
      throw new SearchAbortedError();
    }
    throw err;
  }
  if (isError(raw)) return { hits: [] as SearchHit[], total: 0, tookMs: 0 };
  const hits = Array.isArray(raw.hits) ? raw.hits : Array.isArray(raw.results) ? raw.results : [];
  const tookMs = typeof raw.tookMs === "number" ? raw.tookMs : typeof raw.took === "number" ? raw.took : 0;
  return { hits, total: typeof raw.total === "number" ? raw.total : hits.length, tookMs };
}

/**
 * "Recent pages" for the default palette view. Backed by the new
 * /api/search/(workspace/:id/)recent endpoints — empty-query Meilisearch
 * search sorted by updatedAt desc, restricted to pages/collection_views
 * unless `types` overrides it.
 */
export async function recentWorkspace(
  params: RecentParams & { workspaceId: string },
): Promise<{ hits: SearchHit[]; total: number; tookMs: number }> {
  if (!params.workspaceId) return { hits: [], total: 0, tookMs: 0 };
  return callRecent(`/api/search/workspace/${encodeURIComponent(params.workspaceId)}/recent`, params);
}

export async function recentGlobal(
  params: RecentParams,
): Promise<{ hits: SearchHit[]; total: number; tookMs: number }> {
  return callRecent("/api/search/recent", params);
}

export interface SearchHealthResponse {
  available: boolean;
  inProcessSyncWorker: boolean;
  failClosed: boolean;
}

export async function fetchSearchHealth(): Promise<SearchHealthResponse | null> {
  const res = await getWithAuth<SearchHealthResponse>("/api/search/health");
  if (isError(res)) return null;
  return res;
}
